/* eslint-disable @typescript-eslint/no-unused-vars */
import TitleLayout from '@/components/Layout/TitleLayout';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import RadioButtons from '@oncolens/oncolens-ui/lib/components/FormControls/RadioButtons';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import FormField from '@oncolens/oncolens-ui/lib/libraries/FormField';
import useFormStore from '@oncolens/oncolens-ui/lib/libraries/useFormStore';
import { noop } from 'lodash';
import React, { useEffect, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useNavigate } from 'react-router';
import * as yup from 'yup';
import shallow from 'zustand/shallow';
import { Question, QuestionFormValues, createQuestion, updateQuestion } from '../api/qip';
import { useHydrateQuestion } from '../qip-metrics.hooks';

const schema = yup.object({
  name: yup.string().required('Name is required'),
  displayName: yup.string().nullable(),
  status: yup.string().required('Status is required.'),
  type: yup.string().required('Type is required.'),
});

const defaultValues = {
  name: '',
  displayName: '',
  status: 'Inactive',
  type: '',
} as QuestionFormValues;

export const QuestionBuilder = () => {
  const navigate = useNavigate();
  const formStore = useFormStore(defaultValues, schema);
  const [updateField, updateStore, isValid, type, reset] = formStore(
    (x) => [x.updateField, x.updateStore, x.isValid, x.getValue('type'), x.reset],
    shallow,
  );
  const { questionId } = useHydrateQuestion(updateStore);

  const [saving, setSaving] = useState(false);
  const onSave = () => {
    const store = formStore.getState();

    if (!store.isValid) return;

    setSaving(true);

    const formData = store.getValues();
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { name, displayName, status, type } = formData;

    const data = {
      name,
      displayName: displayName || null,
      status,
      type,
    } as Omit<Question, 'id'>;

    if (!questionId) {
      createQuestion(data)
        .then(() => {
          setSaving(false);
          navigate('/behavior-config/qip-metric-builder/questions');
        })
        .catch((error) => {
          setSaving(false);
          console.error(error);
        });
    } else {
      updateQuestion(questionId as string, { id: questionId, ...data })
        .then(() => {
          setSaving(false);
          navigate('/behavior-config/qip-metric-builder/questions');
        })
        .catch((error) => {
          setSaving(false);
          console.error(error);
        });
    }
  };

  useEffect(() => {
    if (!questionId) {
      reset();
    }
  }, [reset, questionId]);

  return (
    <TitleLayout title={`${questionId ? 'Edit' : 'Add'} Question`}>
      <div className="flex flex-col flex-grow overflow-y-auto bg-white rounded-lg shadow-lg max-w-7xl">
        <div className="grid grid-cols-3 gap-3 p-4 overflow-y-auto flex-grow-1">
          <FormField name="name" store={formStore}>
            {({ value, onChange }) => (
              <TextInput
                id="name"
                label="Name"
                htmlName="name"
                value={value}
                onChange={onChange}
                required
                type="text"
                testSelector="qip-question-builder-name"
              />
            )}
          </FormField>
          <FormField name="type" store={formStore}>
            {({ value, onChange, errorMessage }) => (
              <Listbox
                label="Type"
                data={[
                  {
                    label: 'Free Text',
                    value: 'FreeText',
                  },
                  {
                    label: 'Multiple Choice',
                    value: 'MultipleChoice',
                  },
                ]}
                selected={value}
                setSelected={(val) => {
                  onChange(val);
                  updateField('type', val);
                }}
                required={true}
                error={errorMessage}
                disabled={!!questionId}
                testSelector="qip-question-builder-type"
              />
            )}
          </FormField>
          <FormField name="status" store={formStore}>
            {({ value, onChange }) => (
              <RadioButtons
                label="Status"
                value={value}
                onChange={onChange}
                options={[
                  {
                    label: 'Active',
                    value: 'Active',
                  },
                  {
                    label: 'Inactive',
                    value: 'Inactive',
                  },
                ]}
              />
            )}
          </FormField>
          <FormField name="displayName" store={formStore}>
            {({ value, onChange }) => (
              <TextInput
                id="displayName"
                label="Display Name"
                htmlName="displayName"
                value={value}
                onChange={onChange}
                type="text"
                testSelector="qip-question-builder-display-name"
              />
            )}
          </FormField>
        </div>
        {type === 'MultipleChoice' && (
          <>
            <hr className="mt-8" />
            <div className="grid grid-cols-3 gap-1 px-4 mb-8 flex-grow-1">
              <FormField name="name" store={formStore}>
                {() => (
                  <TextInput
                    id=""
                    label=""
                    htmlName=""
                    value="Yes"
                    onChange={noop}
                    disabled
                    type="text"
                    testSelector="qip-question-builder-yes"
                    wrapperProps={{ className: 'col-span-2' }}
                  />
                )}
              </FormField>
              <FormField name="name" store={formStore}>
                {() => (
                  <TextInput
                    id=""
                    label=""
                    htmlName=""
                    value="No"
                    onChange={noop}
                    disabled
                    type="text"
                    testSelector="qip-question-builder-no"
                    wrapperProps={{ className: 'col-span-2' }}
                  />
                )}
              </FormField>
              <FormField name="name" store={formStore}>
                {() => (
                  <TextInput
                    id=""
                    label=""
                    htmlName=""
                    value="Other"
                    onChange={noop}
                    disabled
                    type="text"
                    testSelector="qip-question-builder-other"
                    wrapperProps={{ className: 'col-span-2' }}
                  />
                )}
              </FormField>
            </div>
          </>
        )}
        <div className="flex items-center w-full pb-4 pr-4 min-h-fit">
          <Button
            className="w-auto px-6 ml-auto mr-3"
            buttonColor="gray"
            onClick={() => navigate(-1)}
            testSelector="qip-question-builder-back-button">
            Back
          </Button>
          <ButtonWithLoading
            loading={saving}
            className="w-auto px-6"
            buttonColor="blue"
            onClick={onSave}
            disabled={!isValid}
            testSelector="qip-question-builder-save-button">
            Save
          </ButtonWithLoading>
        </div>
      </div>
    </TitleLayout>
  );
};
